import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { trackSignup, trackStartTrial } from '../utils/tiktokPixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// This page is hit when Emergent Google OAuth redirects back with #session_id=<sid> in the URL hash.
// We exchange the session_id for our JWT via POST /api/auth/google/session, then route the user
// into the dashboard (or, for new users, into the trial-checkout funnel like the rest of signup).
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setAuthFromResponse } = useAuth();
  // useRef (not useState) prevents React StrictMode double-effect from triggering two exchanges.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      toast.error('Google sign-in did not return a session. Please try again.');
      navigate('/auth', { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        const { token, user, is_new_user } = res.data || {};
        if (!token || !user) {
          throw new Error('Backend did not return token+user');
        }
        setAuthFromResponse(token, user);

        // TikTok Pixel — only on first Google sign-up
        if (is_new_user) {
          try {
            trackSignup({ method: 'google', email: user.email, userId: user.id });
            trackStartTrial({ value: 0, currency: 'EUR', email: user.email, userId: user.id });
          } catch (e) {}
        }

        // Clean the session_id out of the URL bar before routing
        try { window.history.replaceState({}, document.title, '/'); } catch {}

        // New users go through the same paid-trial funnel as email signups
        if (is_new_user && !user.is_founder && user.subscription_plan === 'unpaid') {
          navigate('/subscription?trial=auto', { replace: true });
          toast.success(`Welcome to JobSurf AI, ${user.name?.split(' ')[0] || ''}! Setting up your trial…`);
        } else {
          navigate('/dashboard', { replace: true });
          toast.success(`Welcome back, ${user.name?.split(' ')[0] || ''}!`);
        }
      } catch (err) {
        const msg = err?.response?.data?.detail || 'Could not complete Google sign-in. Please retry.';
        toast.error(msg);
        navigate('/auth', { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-zinc-300 text-sm">Completing Google sign-in…</p>
      </div>
    </div>
  );
}
